import { z } from 'zod';
import { idSchema } from './content-primitives.ts';
import { dnsCardSchema } from './dns-display.ts';
import { validationIssueSchema } from './errors.ts';
export const maxAttachmentBytes = 20 * 1024 * 1024;
export const defaultChatTitle = 'New chat';
export const runProgressEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('activity'), activity: z.string() }),
  z.object({ type: z.literal('text'), itemId: z.string(), delta: z.string(), start: z.boolean() }),
  z.object({
    type: z.literal('reasoning'),
    itemId: z.string(),
    summaryIndex: z.number().int().nonnegative(),
    delta: z.string(),
    start: z.boolean(),
  }),
]);
export const runInputSchema = z
  .object({
    message: z.string().trim().min(1).max(30_000),
    attachmentIds: z.array(idSchema).max(20).default([]),
    runId: idSchema,
    source: z.literal('new-site').optional(),
  })
  .strict();
export const runSummarySchema = z.object({
  id: idSchema,
  chatId: idSchema,
  userId: z.string(),
  status: z.enum(['Running', 'Completed', 'Failed', 'Canceled']),
  message: z.string(),
  attachmentIds: z.array(idSchema),
  validationIssues: z.array(validationIssueSchema).nullable(),
  error: z.object({ code: z.string(), message: z.string(), retryable: z.boolean() }).nullable(),
  createdAt: z.iso.datetime().meta({ pattern: undefined }),
});
export const chatSchema = z.object({
  id: idSchema,
  title: z.string(),
  createdAt: z.iso.datetime().meta({ pattern: undefined }),
  updatedAt: z.iso.datetime().meta({ pattern: undefined }),
});
export const browserScreenshotSchema = z.object({
  type: z.literal('BrowserScreenshot'),
  attachmentId: idSchema,
  caption: z.string(),
  url: z.string(),
});
export const messageDetailsSchema = z.union([
  z.object({
    type: z.literal('Attachments').optional(),
    attachmentIds: z.array(z.string()),
  }),
  z.object({
    type: z.literal('ValidationFeedback').optional(),
    errors: z.array(validationIssueSchema),
    continuing: z.boolean(),
  }),
  dnsCardSchema,
  browserScreenshotSchema,
]);
export const messageSchema = z.object({
  id: idSchema,
  modelItemId: z.string().nullable(),
  role: z.enum(['User', 'Assistant', 'Reasoning']),
  text: z.string(),
  summary: z.array(z.object({ type: z.literal('summary_text'), text: z.string() })).optional(),
  generated: z.boolean(),
  details: messageDetailsSchema.nullable(),
  runId: idSchema,
  createdAt: z.iso.datetime().meta({ pattern: undefined }),
});
export const chatStreamUpdateSchema = z.object({
  run: runSummarySchema.pick({ id: true, status: true, error: true, validationIssues: true }),
  messages: z.array(messageSchema),
});
export type RunProgressEvent = z.infer<typeof runProgressEventSchema>;
export type ChatStreamUpdate = z.infer<typeof chatStreamUpdateSchema>;
